import { Header } from "@/components/navigation/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Download, Container, Shield, Zap, Globe } from "lucide-react";
import { useMemo, useState } from "react";

const Containers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const containers = useMemo(() => ([
    {
      name: "llama2-70b-chat-hf",
      description: "Llama 2 70B Chat optimized for Intel Gaudi 3",
      version: "v1.2.3",
      category: "LLM",
      size: "42.3 GB",
      pulls: "125K",
      verified: true,
      tags: ["llama", "chat", "gaudi3", "optimized"]
    },
    {
      name: "mistral-7b-instruct-v0.2",
      description: "Mistral 7B Instruct with Intel optimizations",
      version: "v2.1.0",
      category: "LLM",
      size: "13.8 GB",
      pulls: "89K",
      verified: true,
      tags: ["mistral", "instruct", "efficient"]
    },
    {
      name: "code-llama-34b-instruct",
      description: "Code Llama 34B for code generation tasks",
      version: "v1.5.1",
      category: "Code AI",
      size: "68.4 GB",
      pulls: "56K",
      verified: true,
      tags: ["codellama", "programming", "generation"]
    },
    {
      name: "falcon-40b-instruct",
      description: "Falcon 40B Instruct with Dell infrastructure tuning",
      version: "v1.0.8",
      category: "LLM",
      size: "81.2 GB",
      pulls: "34K",
      verified: false,
      tags: ["falcon", "large", "instruct"]
    },
    {
      name: "whisper-large-v3-gaudi",
      description: "OpenAI Whisper Large v3 optimized for Gaudi 3",
      version: "v3.2.1",
      category: "Speech AI",
      size: "3.1 GB",
      pulls: "67K",
      verified: true,
      tags: ["whisper", "speech", "transcription"]
    },
    {
      name: "stable-diffusion-xl-gaudi",
      description: "Stable Diffusion XL for image generation on Gaudi 3",
      version: "v2.4.0",
      category: "Vision AI",
      size: "6.8 GB",
      pulls: "45K",
      verified: true,
      tags: ["diffusion", "image", "generation"]
    }
  ]), []);

  const categories = useMemo(() => {
    const base = [
      { name: "All", count: containers.length },
      { name: "LLM", count: containers.filter(c => c.category === "LLM").length },
      { name: "Code AI", count: containers.filter(c => c.category === "Code AI").length },
      { name: "Speech AI", count: containers.filter(c => c.category === "Speech AI").length },
      { name: "Vision AI", count: containers.filter(c => c.category === "Vision AI").length }
    ];
    return base.filter(category => category.count > 0 || category.name === "All");
  }, [containers]);

  const normalizedQuery = searchQuery.toLowerCase();
  const filteredContainers = containers.filter(container => {
    const matchesCategory = activeCategory === "All" || container.category === activeCategory;
    if (!matchesCategory) return false;

    if (!normalizedQuery) return true;

    return (
      container.name.toLowerCase().includes(normalizedQuery) ||
      container.description.toLowerCase().includes(normalizedQuery) ||
      container.tags.some(tag => tag.toLowerCase().includes(normalizedQuery))
    );
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Container Catalog</h1>
              <p className="text-muted-foreground text-lg">
                Pre-built inference containers optimized for Intel Gaudi 3 and Dell infrastructure
              </p>
            </div>

            <Tabs defaultValue="containers" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 lg:w-400">
                <TabsTrigger value="containers">Browse Containers</TabsTrigger>
                <TabsTrigger value="builder">Container Builder</TabsTrigger>
              </TabsList>

              <TabsContent value="containers" className="space-y-6">
                {/* Search and Filters */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search containers, tags, or descriptions..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {categories.map((category) => (
                          <Badge
                            key={category.name}
                            variant={activeCategory === category.name ? "default" : "outline"}
                            className="cursor-pointer hover:bg-muted"
                            onClick={() => setActiveCategory(category.name)}
                            aria-pressed={activeCategory === category.name}
                          >
                            {category.name} ({category.count})
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Containers Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredContainers.map((container) => (
                    <Card key={container.name} className="hover:shadow-glow transition-smooth">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Container className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <CardTitle className="text-base truncate">{container.name}</CardTitle>
                              <div className="flex items-center space-x-2 mt-1">
                                <Badge variant="secondary">{container.category}</Badge>
                                {container.verified && (
                                  <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
                                    <Shield className="h-3 w-3 mr-1" />
                                    Verified
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">
                          {container.description}
                        </CardDescription>
                        
                        <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                          <div>Version: <span className="font-medium">{container.version}</span></div>
                          <div>Size: <span className="font-medium">{container.size}</span></div>
                          <div>Downloads: <span className="font-medium">{container.pulls}</span></div>
                          <div className="flex items-center">
                            <Zap className="h-3 w-3 mr-1 text-primary" />
                            <span className="font-medium">Optimized</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1 mb-4">
                          {container.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {container.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{container.tags.length - 3} more
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <Button variant="outline" size="sm" className="gap-2">
                            <Globe className="h-4 w-4" />
                            View Details
                          </Button>
                          <Button size="sm" className="gap-2">
                            <Download className="h-4 w-4" />
                            Pull
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="builder" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Container Builder</CardTitle>
                    <CardDescription>
                      Build custom inference containers optimized for your specific models and hardware
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="text-center py-12">
                        <Container className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">Custom Container Builder</h3>
                        <p className="text-muted-foreground mb-6">
                          Coming soon - Build optimized containers with our guided wizard
                        </p>
                        <Button variant="outline" disabled>
                          Launch Builder
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Containers;